export type UserPresence = {
  userId: number;
  status: number;
};

export type UserPresenceMap = Record<number, number>;

/** Normalize one bounded IM presence request without mutating caller input. */
export function normalizePresenceSubjects(userIds: number[]): number[] {
  const uniqueUserIds = [...new Set(userIds)];
  if (uniqueUserIds.length === 0 || uniqueUserIds.length > 200 ||
      uniqueUserIds.some((userId) => !Number.isSafeInteger(userId) || userId <= 0)) {
    throw new Error('Presence request requires 1 to 200 unique positive user ids');
  }
  return uniqueUserIds;
}

/**
 * Apply one ordered notification to the volatile presence index.
 *
 * Presence is keyed independently from contacts because a direct conversation
 * can exist without a friendship. Keeping this map separate also prevents
 * socket-scoped state from leaking into the canonical profile cache.
 */
export function applyPresenceNotification(
  current: UserPresenceMap,
  presence: UserPresence,
): UserPresenceMap {
  if (current[presence.userId] === presence.status) return current;
  return {
    ...current,
    [presence.userId]: presence.status,
  };
}

/**
 * Apply a response snapshot without overwriting notifications received while
 * the request was in flight. A differing per-user revision means the realtime
 * notification is newer and must win over that snapshot row.
 */
export function applyPresenceSnapshot(
  current: UserPresenceMap,
  snapshot: UserPresence[],
  revisionsAtRequest: ReadonlyMap<number, number>,
  currentRevisions: ReadonlyMap<number, number>,
): UserPresenceMap {
  let changed = false;
  const next = { ...current };
  for (const presence of snapshot) {
    if ((currentRevisions.get(presence.userId) ?? 0) !==
        (revisionsAtRequest.get(presence.userId) ?? 0)) continue;
    if (next[presence.userId] !== presence.status) {
      next[presence.userId] = presence.status;
      changed = true;
    }
  }
  return changed ? next : current;
}
